/**
 * Shared event handlers for editable cell inputs
 * Centralizes keyboard and blur event handling to avoid duplication
 */

import React from "react";
import { getIsClickingButton, setIsClickingButton } from "./inline-edit-controls";

/**
 * Options for createKeyDownHandler
 */
export interface KeyDownHandlerOptions {
  onSave: (rowId: string, fieldId: string) => void;
  onCancel: () => void;
  rowId: string;
  fieldId: string;
}

/**
 * Creates a keydown handler for manual-save inputs (text, date, datetime, number)
 * Enter key triggers save, Escape key triggers cancel
 */
export const createKeyDownHandler = ({
  onSave,
  onCancel,
  rowId,
  fieldId,
}: KeyDownHandlerOptions): ((e: React.KeyboardEvent<HTMLInputElement>) => void) => {
  return (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onSave(rowId, fieldId);
    } else if (e.key === "Escape") {
      onCancel();
    }
  };
};

/**
 * Options for createBlurSaveHandler
 */
export interface BlurSaveHandlerOptions {
  onSave: (rowId: string, fieldId: string) => void;
  rowId: string;
  fieldId: string;
  cellKey: string;
}

/**
 * Creates a blur handler for manual-save inputs (text, date, datetime, number)
 * Auto-saves on blur with a small delay, but skips if a button is being clicked
 * This enables click-away functionality while preventing conflicts with save/cancel buttons
 */
export const createBlurSaveHandler = ({
  onSave,
  rowId,
  fieldId,
  cellKey,
}: BlurSaveHandlerOptions): (() => void) => {
  return () => {
    // Don't auto-save if clicking on a button (cancel/save)
    if (getIsClickingButton(cellKey)) {
      setIsClickingButton(cellKey, false);
      return;
    }
    setTimeout(() => onSave(rowId, fieldId), 100);
  };
};

/**
 * Options for createTextareaKeyDownHandler
 */
export interface TextareaKeyDownHandlerOptions {
  onCancel: () => void;
}

/**
 * Creates a keydown handler for textarea inputs
 * Only Escape cancels; Enter inserts a newline (default textarea behavior)
 */
export const createTextareaKeyDownHandler = ({
  onCancel,
}: TextareaKeyDownHandlerOptions): ((e: React.KeyboardEvent<HTMLTextAreaElement>) => void) => {
  return (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Escape") {
      onCancel();
    }
  };
};

/**
 * Options for createTextareaBlurSaveHandler
 */
export interface TextareaBlurSaveHandlerOptions {
  onSave: (rowId: string, fieldId: string) => Promise<void>;
  onCancel: () => void;
  rowId: string;
  fieldId: string;
  value: unknown;
  editValue: string;
}

/**
 * Creates a blur handler for textarea inputs
 * Auto-saves on blur only when the value has actually changed
 */
export const createTextareaBlurSaveHandler = ({
  onSave,
  rowId,
  fieldId,
  value,
  editValue,
}: TextareaBlurSaveHandlerOptions): (() => void) => {
  return () => {
    const currentValue = String(value || "");
    if (editValue !== currentValue) {
      setTimeout(() => {
        onSave(rowId, fieldId).catch((error) => {
          console.error("Error auto-saving textarea:", error);
        });
      }, 100);
    }
  };
};
